import { RouteComponentProps } from "react-router";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Col, Divider, Form, Popover, Row, Slider, Table, Tag } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Carousel } from "radar/components";
import { useEffect, useState } from "react";
import { srcToBase64 } from "shared/helpers/srcToBase64";
import {
  addArtistsToFollow,
  fetchFollowedArtists,
  fetchRecentlyAccessedArtists,
  removeArtistsFromFollow,
} from "shared/service/StrmService";
import AddArtistCard from "./AddArtistCard";
import ArtistCard from "./ArtistCard";
import { ColumnsType } from "antd/lib/table";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import WelcomeModal from "./WelcomeModal";
import FollowersModal from "./FollowersModal";
import { fetchArtistRank } from "radar/Service/RadarService";
import { FieldSelect } from "shared/components";
import { RADAR_HOME_FILTER } from "shared/constants/LocalStorageConsts";
import { getCapitalizedLang } from "shared/helpers/capitalized-lang";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { MonitoredDataState } from "explorer/reducers/monitored-data";
import "./Home.scss";

const Home: React.FC<RouteComponentProps> = () => {
  const { t, i18n } = useTranslation("radar");
  const { loggedUser } = useSelector((state: any) => state.users);
  const { genres, genreOptions } = useSelector((state: any) => state.genres);
  const { countries, countryOptions } = useSelector(
    (state: any) => state.countries
  );
  const { totalMonitoredArtists } = useSelector(
    (state: MonitoredDataState) => state.monitoredData
  );

  const defaults = {
    defaultForm: {
      country: [loggedUser?.countryCode || "BR"],
      genre: [],
      genreSpotify: [],
      stage: [1, 10],
    },
    defaultFilter: {
      country: [loggedUser?.countryCode || "BR"],
      genre: [],
      stageInit: 1,
      stageEnd: 10,
      offset: 0,
      limit: 10,
      artistSortMode: "growth_speed",
      includeMetaData: true,
    },
  };

  const initial = () => {
    const savedFilter = localStorage.getItem(RADAR_HOME_FILTER);

    if (savedFilter) {
      const parsed = JSON.parse(savedFilter);

      const { stageInit, stageEnd, ...rest } = parsed;
      return {
        defaultForm: {
          ...rest,
          stage: [stageInit, stageEnd],
        },
        defaultFilter: parsed,
      };
    }

    return {
      ...defaults,
    };
  };

  const { defaultFilter, defaultForm } = initial();
  const [form] = Form.useForm();
  const [showModalWelcome, setShowModalWelcome] = useState<boolean>(false);
  const [growthList, setGrowthList] = useState<any>([]);
  const [imageList, setImageList] = useState<any>([]);
  const [musicList, setMusicList] = useState<any>([]);
  const [highlightFiler, sethighlightFiler] = useState<any>(defaultFilter);
  const [loadingFollowedHighlighted, setLoadingFollowedHighlighted] =
    useState<boolean>(false);
  const [loadingRecentlyFollowed, setLoadingRecentlyFollowed] =
    useState<boolean>(false);
  const [loadingHighlightedArtists, setLoadingHighlightedArtists] =
    useState<boolean>(false);
  const [followedArtistsMetadata, setFollowedArtistsMetadata] = useState<any[]>(
    []
  );
  const [recentlyAccessedArtistsMetadata, setRecentlyAccessedArtistsMetadata] =
    useState<any[]>([]);
  const [highlightArtists, setHighlightArtists] = useState<any[]>([]);
  const [limitArtists] = useState(100);

  const wrappGetI18nGenre = (genre: string) =>
    getI18nGenre(genre, i18n, genres);

  const getI18nCoutry = (country: string) => {
    if (country) {
      const countryI18n = countries.find((gen: any) => gen.code === country);
      if (countryI18n)
        return countryI18n[`description${getCapitalizedLang(i18n)}`];
      return country;
    }
    return "";
  };

  const onCloseWelcomeModal = () => {
    setShowModalWelcome(false);
  };

  const columns: ColumnsType<any> = [
    {
      title: t("pages.ArtistsRanking.artistName"),
      dataIndex: "artistName",
      width: "15%",
    },
    {
      title: t("pages.ArtistsRanking.genres"),
      dataIndex: "genresStrm",
      width: "15%",
      render: (value) =>
        value?.map((genre: any) => (
          <Tag
            key={Math.random()}
            color="transparent"
            style={{
              border: "2px solid #E4BC5C",
              color: "#E4BC5C",
              padding: "0px 8px",
            }}
          >
            {wrappGetI18nGenre(genre)}
          </Tag>
        )),
    },
    {
      title: t("pages.ArtistsRanking.country"),
      dataIndex: "country",
      align: "center",
      width: "10%",
      render: (value: string) => <>{getI18nCoutry(value)}</>,
    },
    {
      title: t("pages.ArtistsRanking.stage"),
      dataIndex: "careerStage",
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.development"),
      dataIndex: "developmentIndex",
      render: (value) => <>{(value || value === 0) && value.toPrecision(3)}</>,
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.performance"),
      dataIndex: "performanceIndex",
      render: (value) => <>{(value || value === 0) && value.toPrecision(3)}</>,
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.popularityIndex"),
      dataIndex: "popularityIndex",
      render: (value) => (
        <>{!isNaN(value) && (value || value === 0) && value.toPrecision(3)}</>
      ),
      align: "center",
      width: "10%",
    },
  ];

  const updateFollowedArtists = async () => {
    setLoadingFollowedHighlighted(true);
    const followedArtistsResponse: any = await fetchFollowedArtists();
    setGrowthList(followedArtistsResponse.growthList);
    setImageList(followedArtistsResponse.imageList);
    setMusicList(followedArtistsResponse.musicList);
    const followedArtistsWithMetadataList = await Promise.all(
      followedArtistsResponse.followingList.map(async (artist: any) => ({
        ...artist,
        imageUrl: await srcToBase64(artist.imageUrl),
      }))
    );
    setFollowedArtistsMetadata(followedArtistsWithMetadataList);
    setLoadingFollowedHighlighted(false);
  };

  const onAddArtistToFollow = async (artistId: string) => {
    const response = await addArtistsToFollow(artistId);
    if (response) {
      updateFollowedArtists();
    }
  };

  const onRemoveArtistClick = async (artistId: string) => {
    const response = await removeArtistsFromFollow(artistId);
    if (response) {
      updateFollowedArtists();
    }
  };

  const getFirstArtist = (artistList: any[]) => {
    if (artistList.length > 0) return artistList[0];
    else return {};
  };

  const onFinishFormHighlightArtists = async () => {
    const { stage, ...rest } = form.getFieldsValue(true);
    const [stageInit, stageEnd] = stage;
    const saveFilter = {
      ...highlightFiler,
      ...rest,
      stageInit,
      stageEnd,
    };
    localStorage.setItem(RADAR_HOME_FILTER, JSON.stringify(saveFilter));
    sethighlightFiler(saveFilter);
  };

  useEffect(() => {
    const getArtists = async () => {
      setLoadingFollowedHighlighted(true);
      setLoadingRecentlyFollowed(true);
      const recentlyArtistsResponse: any = await fetchRecentlyAccessedArtists();

      if (recentlyArtistsResponse) {
        const recentlyAccessedArtistsWithMetadataList = await Promise.all(
          recentlyArtistsResponse
            ?.map(async (artist: any) => ({
              ...artist,
              imageUrl: await srcToBase64(artist.imageUrl),
            }))
            .filter((artist: any) => artist.image !== "")
        );
        setRecentlyAccessedArtistsMetadata(
          recentlyAccessedArtistsWithMetadataList
        );
      }

      setLoadingRecentlyFollowed(false);

      try {
        const followedArtistsResponse: any = await fetchFollowedArtists();
        setGrowthList(followedArtistsResponse.growthList);
        setImageList(followedArtistsResponse.imageList);
        setMusicList(followedArtistsResponse.musicList);
        const followedArtistsWithMetadataList = await Promise.all(
          followedArtistsResponse.followingList
            .map(async (artist: any) => ({
              ...artist,
              imageUrl: await srcToBase64(artist.imageUrl),
            }))
            .filter((artist: any) => artist.image !== "")
        );
        setFollowedArtistsMetadata(followedArtistsWithMetadataList);
      } catch (error) {
        setLoadingFollowedHighlighted(false);
      }
      setLoadingFollowedHighlighted(false);
    };

    getArtists();
  }, []);

  useEffect(() => {
    const getHighlightedArtists = async () => {
      setLoadingHighlightedArtists(true);
      const result = await fetchArtistRank(highlightFiler);
      const artistsWithMetadataList = await Promise.all(
        result?.artists?.artists?.map(async (artist: any) => ({
          ...artist,
          imageUrl: await srcToBase64(artist.imageUrl),
        }))
      );
      setHighlightArtists(artistsWithMetadataList);
      setLoadingHighlightedArtists(false);
    };
    getHighlightedArtists();
  }, [highlightFiler]);

  useEffect(() => {
    if (loggedUser.firstLoginInvestorsAt === loggedUser.lastLoginInvestorsAt)
      setShowModalWelcome(true);
  }, [loggedUser]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Home.S4L_HOME,
    });
  }, []);

  return (
    <div className="home">
      <h1 className="title main-color-letter ">
        {t("pages.Home.welcome", { name: loggedUser?.name || "" })}
      </h1>
      <p className="intro">
        {t("pages.Home.intro", {
          limitArtists,
          totalArtists: totalMonitoredArtists,
        })}
      </p>
      <Divider />
      {showModalWelcome && <WelcomeModal onClose={onCloseWelcomeModal} />}

      <div className="blur">
        <Row>
          <Col xs={24}>
            <div className="flex-align-items-center">
              <h3 className="sub-title main-color-letter ">
                {t("pages.Home.recentlyAccessed")}
              </h3>
              <Popover
                showArrow={false}
                content={<> {t("pages.Home.recentlyAccessedTooltip")}</>}
                placement="right"
              >
                <InfoCircleOutlined
                  style={{ fontSize: 20, color: "#455FE4" }}
                />
              </Popover>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Carousel
              visibleItems={4.5}
              key={`RECENTLY_ACCESSED`}
              loading={loadingRecentlyFollowed}
            >
              {recentlyAccessedArtistsMetadata.map((artist: any) => (
                <ArtistCard
                  key={`recently_accessed_${artist.artistIdStrm}`}
                  {...artist}
                />
              ))}
            </Carousel>
          </Col>
        </Row>
        <Row style={{ marginTop: 32 }}>
          <Col xs={24}>
            <Row justify="space-between">
              <div className="flex-align-items-center">
                <h3 className="sub-title main-color-letter ">
                  {t("pages.Home.followedArtists")}
                </h3>
                <Popover
                  showArrow={false}
                  content={<> {t("pages.Home.followedArtistsTooltip")}</>}
                  placement="right"
                >
                  <InfoCircleOutlined
                    style={{ fontSize: 20, color: "#455FE4" }}
                  />
                </Popover>
              </div>
              <div className="buttons-following">
                <button
                  className="botao-azul-degrade-vazado"
                  style={{ cursor: "default" }}
                >
                  {t("pages.Home.followedCount", {
                    count: followedArtistsMetadata.length,
                  })}
                </button>
                <Popover
                  showArrow={false}
                  overlayClassName="followers-tooltip"
                  content={
                    <FollowersModal
                      artists={followedArtistsMetadata}
                      onRemove={onRemoveArtistClick}
                    />
                  }
                  placement="left"
                >
                  <button className="botao-vazado">
                    {t("pages.Home.seeAll")}
                  </button>
                </Popover>
              </div>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <AddArtistCard
              onAdd={onAddArtistToFollow}
              blocked={followedArtistsMetadata.length >= limitArtists}
            />
          </Col>
          <Col xs={18}>
            <Carousel visibleItems={2.5} loading={loadingFollowedHighlighted}>
              {followedArtistsMetadata.map((artist: any) => (
                <ArtistCard
                  key={`followed_artists_${artist.artistIdStrm}`}
                  {...artist}
                  onRemove={onRemoveArtistClick}
                />
              ))}
            </Carousel>
          </Col>
        </Row>
        <Row style={{ marginTop: 32 }}>
          <Col xs={24}>
            <div className="flex-align-items-center">
              <h3 className="sub-title main-color-letter ">
                {t("pages.Home.followedHightlightedArtists")}
              </h3>
              <Popover
                showArrow={false}
                content={
                  <> {t("pages.Home.followedHightlightedArtistsTooltip")}</>
                }
                placement="right"
              >
                <InfoCircleOutlined
                  style={{ fontSize: 20, color: "#455FE4" }}
                />
              </Popover>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={6} style={{ margin: "12px 0" }}>
            <Popover
              showArrow={false}
              content={<> {t("pages.Home.growingTooltip")}</>}
              placement="right"
            >
              <span className="button-like"> {t("pages.Home.growing")}</span>
            </Popover>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <ArtistCard
              {...getFirstArtist(growthList)}
              position={1}
              loading={loadingFollowedHighlighted}
            />
          </Col>
          <Col xs={18}>
            <Table
              rowKey={(record: any) => record.artistIdStrm}
              columns={columns}
              dataSource={growthList}
              pagination={{ pageSize: 5 }}
              loading={loadingFollowedHighlighted}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 32 }}>
          <Col xs={6} style={{ margin: "12px 0" }}>
            <Popover
              showArrow={false}
              content={<> {t("pages.Home.musicTooltip")}</>}
              placement="right"
            >
              <span className="button-like"> {t("pages.Home.music")}</span>
            </Popover>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <ArtistCard
              {...getFirstArtist(musicList)}
              position={1}
              loading={loadingFollowedHighlighted}
            />
          </Col>
          <Col xs={18}>
            <Table
              rowKey={(record: any) => record.artistIdStrm}
              columns={columns}
              dataSource={musicList}
              pagination={{ pageSize: 5 }}
              loading={loadingFollowedHighlighted}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 32 }}>
          <Col xs={6} style={{ margin: "12px 0" }}>
            <Popover
              showArrow={false}
              content={<> {t("pages.Home.imageTooltip")}</>}
              placement="right"
            >
              <span className="button-like"> {t("pages.Home.image")}</span>
            </Popover>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <ArtistCard
              {...getFirstArtist(imageList)}
              position={1}
              loading={loadingFollowedHighlighted}
            />
          </Col>
          <Col xs={18}>
            <Table
              rowKey={(record: any) => record.artistIdStrm}
              columns={columns}
              dataSource={imageList}
              pagination={{ pageSize: 5 }}
              loading={loadingFollowedHighlighted}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 32 }}>
          <Col xs={12}>
            <div className="flex-align-items-center">
              <h3 className="sub-title main-color-letter ">
                {t("pages.Home.highlightedGrowingSpeed")}
              </h3>
              <Popover
                showArrow={false}
                content={<> {t("pages.Home.highlightedGrowingSpeedTooltip")}</>}
                placement="right"
              >
                <InfoCircleOutlined
                  style={{ fontSize: 20, color: "#455FE4" }}
                />
              </Popover>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12} style={{ margin: "12px 0" }}>
            <Popover
              showArrow={false}
              content={<> {t("pages.Home.growthSpeedTooltip")}</>}
              placement="right"
            >
              <span className="button-like">{t("pages.Home.growthSpeed")}</span>
            </Popover>
          </Col>
          <Col xs={12}>
            <Form
              form={form}
              layout="vertical"
              initialValues={defaultForm}
              onFinish={onFinishFormHighlightArtists}
            >
              <Row gutter={16}>
                <Col xs={6}>
                  <Form.Item
                    name="genre"
                    label={t("pages.ArtistsRanking.genre")}
                  >
                    <FieldSelect
                      mode="multiple"
                      showSelectAll
                      dataList={genreOptions}
                    />
                  </Form.Item>
                </Col>
                <Col xs={6}>
                  <Form.Item
                    name="country"
                    label={t("pages.ArtistsRanking.country")}
                  >
                    <FieldSelect
                      mode="multiple"
                      showSelectAll={false}
                      optionFilterProp="children"
                      dataList={countryOptions}
                      filterOption={(input: string, option: any) =>
                        option?.children
                          ?.toUpperCase()
                          ?.includes(input.toUpperCase())
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={6}>
                  <Form.Item
                    name="stage"
                    label={t("pages.ArtistsRanking.stage")}
                  >
                    <Slider
                      min={1}
                      max={10}
                      range
                      tooltipPlacement="bottomLeft"
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={6}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <button type="submit" className="botao-branco">
                    {t("pages.ArtistsRanking.filter")}
                  </button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Carousel
              visibleItems={4.5}
              key={`HIGHLIGHTED_ARTISTS`}
              loading={loadingHighlightedArtists}
            >
              {highlightArtists.map((artist: any) => (
                <ArtistCard
                  key={`highlighted_${artist.artistIdStrm}`}
                  {...artist}
                />
              ))}
            </Carousel>
          </Col>
        </Row>
        <Row style={{ marginTop: 32 }} hidden>
          <Col xs={12}>
            <div className="flex-align-items-center">
              <h3 className="sub-title main-color-letter ">
                {t("pages.Home.topTracksArtists")}
              </h3>
              <Popover
                showArrow={false}
                content={<> {t("pages.Home.topTracksArtistsTooltip")}</>}
                placement="right"
              >
                <InfoCircleOutlined
                  style={{ fontSize: 20, color: "#455FE4" }}
                />
              </Popover>
            </div>
          </Col>
        </Row>
        <Row hidden>
          <Col xs={24}>
            <Carousel visibleItems={4.5} key={`RECENTLY_ACCESSED`}></Carousel>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Home;
